import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RecordType, CreateWorkLogModel, ManufacturingOperationDetailModel } from '@core/api/models/timesheets';
import { ManufacturingOperationDataService } from '@core/api/services/timesheets/manufacturing-operation-data.service';
import { AuthService } from '@core/modules/auth';
import { SnackbarService } from '@core/services/snackbar.service';
import { DailyPlansService } from '@features/manufactory/services/daily-plans.service';
import { RequestForChangeType } from '@features/technology/shared/services/request-for-change.model';
import { map, ReplaySubject, Subject, switchMap, tap } from 'rxjs';

export interface LocksModalData {
  operationId: string;
  amountOfManufacturedPieces: number;
  locks: (number | null)[];
}

const timeTrackerLocalStorageKey = 'time-tracker-tasks';

export interface TimeTrackerItem {
  taskNumber: string;
  start?: string;
  paused?: string;
  triggeredFrom?: string;
  // Work was already logged by a stop whose state change failed - the next stop only changes the state
  stopPending?: boolean;
  meta?: any;
}

export interface TaskMetadata {
  dailyPlanId: string;
  technologicalProcessId: string;
  operationNumber: number;
}

@Injectable({
  providedIn: 'root',
})
export class TimeTrackerNewService {
  private readonly timesheetService = inject(ManufacturingOperationDataService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly dailyPlanService = inject(DailyPlansService);
  private readonly authService = inject(AuthService);

  private tasks = new ReplaySubject<TimeTrackerItem[]>();
  private _tasks!: TimeTrackerItem[];

  // `revert` returns the tracker to the state matching the operation when its state change fails
  private onStart = new Subject<{ id: string; taskNumber: string; state: boolean; revert: () => void }>();
  private onPause = new Subject<{ id: string; taskNumber: string; revert: () => void }>();
  private onStop = new Subject<{ id: string; taskNumber: string; revert: () => void }>();
  private reload = new Subject<string | undefined>();
  private locksModal = new Subject<LocksModalData>();
  private processingTasks = signal<string[]>([]);
  private actionsInProgress = signal<string[]>([]);
  private openPopup = new Subject<void>();
  private closePopup = new Subject<void>();

  public onStart$ = this.onStart.asObservable();
  public onPause$ = this.onPause.asObservable();
  public onStop$ = this.onStop.asObservable();
  public reload$ = this.reload.asObservable();
  public locksModal$ = this.locksModal.asObservable();

  public openLocksModal(data: LocksModalData): void {
    this.locksModal.next(data);
  }
  public openPopup$ = this.openPopup.asObservable();
  public closePopup$ = this.closePopup.asObservable();
  public workInProgress$ = this.tasks.pipe(map(tasks => tasks.some(t => !!t.start && t.paused !== 'true')));
  private _workInProgress = toSignal(this.workInProgress$);
  public workInProgress = computed(() => this._workInProgress());
  public taskInProgress$ = this.tasks.pipe(
    map(tasks =>
      tasks.reduce((arr: any, task) => {
        arr[task.taskNumber] = !!task.start && task.paused !== 'true';
        return arr;
      }, {}),
    ),
  );
  private _tasksInProgress = toSignal(
    this.tasks.pipe(map(tasks => tasks.filter(task => !!task.start && task.paused !== 'true').map(t => t.taskNumber))),
  );
  public tasksInProgress = computed(() => this._tasksInProgress());
  public isActive$ = this.tasks.pipe(map(d => d.length > 0));
  public tryingToLeave = signal(false);

  // Popup
  public overclaimedWorklogPopupVisible = signal<boolean>(false);
  public alreadyOverclaimedWorklogPopupVisible = signal<boolean>(false);
  public leavePopupVisible = signal<boolean>(false);
  public requestForChangeMinutes = 0;
  public manufacturingOperation!: ManufacturingOperationDetailModel;
  private currentTaskMetadata!: TaskMetadata;

  constructor() {
    this.tasks.subscribe(tasks => {
      this._tasks = tasks;
      this.saveDataToLocalStorage(tasks);
    });
    this.tasks.next(this.getDataFromLocalStorage());

    effect(() => {
      if (this.processingTasks().length === 0 && this.tryingToLeave()) {
        this.authService.logout();
      }
    });
  }

  public doReload(dailyPlanId: string) {
    this.reload.next(dailyPlanId);
  }

  public finishedProcessing(taskNumber: string) {
    const foundTask = this.processingTasks().findIndex(task => task === taskNumber);
    if (foundTask > -1) {
      const arr = [...this.processingTasks()];
      arr.splice(foundTask, 1);
      this.processingTasks.set(arr);
    }
  }

  /**
   * Marks start/pause/stop of the task as running, so its buttons stay locked until the whole chain finishes
   * @returns false when another action for the task is still running
   */
  public beginAction(taskNumber: string) {
    if (this.actionsInProgress().includes(taskNumber)) return false;
    this.actionsInProgress.update(tasks => [...tasks, taskNumber]);
    return true;
  }

  public endAction(taskNumber: string) {
    this.actionsInProgress.update(tasks => tasks.filter(task => task !== taskNumber));
  }

  public isActionInProgress(taskNumber: string) {
    return this.actionsInProgress().includes(taskNumber);
  }

  /* Start specified task */
  public startWorkTask(task: TimeTrackerItem, cb?: () => void) {
    const revert = this.getStartRevert(task);
    this.saveTask({ ...task, start: Date.now().toString(), paused: 'false' });

    setTimeout(() => {
      // need to wait before panel shown up to trigger open
      this.openPopup.next();
    }, 0);

    if (task.triggeredFrom === 'daily-plan') {
      this.onStart.next({ id: task.meta.dailyPlanId, taskNumber: task.taskNumber, state: false, revert });
    } else {
      this.endAction(task.taskNumber);
    }
    cb && cb();
  }

  /* Continue specified task */
  public continueWorkTask(task: TimeTrackerItem, operation: ManufacturingOperationDetailModel, cb?: () => void) {
    const revert = this.getStartRevert(task);
    this.saveTask({ ...task, start: Date.now().toString(), paused: 'false' });

    if (task.triggeredFrom === 'daily-plan') {
      this.onStart.next({ id: task.meta.dailyPlanId, taskNumber: task.taskNumber, state: true, revert });
    } else {
      this.endAction(task.taskNumber);
    }
    cb && cb();
  }

  /* Pause specified task */
  public pauseWorkTask(task: TimeTrackerItem, overclaimedMinutesCallback?: (overclaimedMinutes?: number) => void) {
    this.timesheetService.getOperationByTaskNumber(task.taskNumber).subscribe({
      next: (operation: ManufacturingOperationDetailModel) => {
        this.manufacturingOperation = operation;

        this.logWork(
          task,
          operation,
          false,
          () => {
            this.saveTask({ ...task, start: Date.now().toString(), paused: 'true' });
            if (task.triggeredFrom === 'daily-plan') {
              this.onPause.next({
                id: task.meta.dailyPlanId,
                taskNumber: task.taskNumber,
                revert: () => this.keepTaskRunning(task),
              });
            } else {
              this.endAction(task.taskNumber);
            }
          },
          overclaimedMinutesCallback,
        );
      },
      error: () => this.onLogWorkFailed(task.taskNumber),
    });
  }

  /* Stop specified task */
  public stopWorkTask(task: TimeTrackerItem, overclaimedMinutesCallback?: (overclaimedMinutes?: number) => void) {
    if (this.getTask(task.taskNumber)?.stopPending) {
      this.completeStop(task);
      return;
    }

    this.timesheetService.getOperationByTaskNumber(task.taskNumber).subscribe({
      next: (operation: ManufacturingOperationDetailModel) => {
        this.manufacturingOperation = operation;
        this.logWork(task, operation, true, () => this.completeStop(task), overclaimedMinutesCallback);
      },
      error: () => this.onLogWorkFailed(task.taskNumber),
    });
  }

  /* Revert work time for specified task */
  public revertWorkTask(task: TimeTrackerItem, operation: ManufacturingOperationDetailModel) {
    this.saveTask({ ...task, start: Date.now().toString(), paused: 'true' });
  }

  /* Return task for given taskNumber */
  public getTask(taskNumber: string) {
    return this._tasks.find(t => t.taskNumber === taskNumber);
  }

  /**
   * Add or updates already existing task to tasks array
   * @param task Task
   */
  public saveTask(task: TimeTrackerItem) {
    const foundTaskIndex = this.getTaskIndex(task.taskNumber!);
    if (foundTaskIndex !== -1) {
      this._tasks[foundTaskIndex] = { ...this._tasks[foundTaskIndex], ...task };
      this.tasks.next(this._tasks);
    } else {
      this.tasks.next([...this._tasks, task]);
    }
  }

  /**
   * Removes task from tasks array
   * @param task Task
   */
  public removeTask(task: TimeTrackerItem) {
    const foundTaskIndex = this.getTaskIndex(task.taskNumber!);
    if (foundTaskIndex !== -1) {
      this._tasks.splice(foundTaskIndex, 1);
      this.tasks.next(this._tasks);
    }
  }

  public getTaskIndex(taskNumber: string) {
    return this._tasks.findIndex(t => t.taskNumber === taskNumber);
  }

  public getTasks() {
    return this.tasks.asObservable();
  }

  public closeOverLimitWorklogModal() {
    this.overclaimedWorklogPopupVisible.set(false);
    this.alreadyOverclaimedWorklogPopupVisible.set(false);
  }

  public closeTimeTrackerModal() {
    this.closePopup.next();
  }

  public showLeaveModal() {
    this.leavePopupVisible.set(true);
  }

  public closeLeaveModal() {
    this.leavePopupVisible.set(false);
  }

  public handleLeaveWithoutLogWork() {
    this.closeLeaveModal();
    this.authService.logout();
  }

  public handleCreateRequestForChange(description: string) {
    this.dailyPlanService
      .detail(this.currentTaskMetadata.dailyPlanId)
      .pipe(
        switchMap(entity => {
          const operation = entity.operation as any;
          const preparationTime =
            operation.requestForChangePreparationTime > 0 ? operation.requestForChangePreparationTime : operation.preparationTimeMinutes;
          const stepTime = operation.requestForChangeStepTime > 0 ? operation.requestForChangeStepTime : operation.stepTime;
          const newStepTime = stepTime + this.requestForChangeMinutes;
          return this.createRequestForChangeTimeComplaint(description, Math.ceil(newStepTime), Math.ceil(preparationTime));
        }),
      )
      .subscribe({
        next: () => {
          this.requestForChangeMinutes = 0;
          this.closeOverLimitWorklogModal();
          this.snackbarService.addSnackbarAndTranslateIt({
            message: 'common.dailyPlanDetail.complaintCreationSuccess',
            iconType: 'success',
          });
        },
      });
  }

  /* Logout is waiting for tasks to be paused - give it up when a pause fails */
  public cancelLeave() {
    this.tryingToLeave.set(false);
  }

  public handleLeaveAndLogWork() {
    this.processingTasks.set(this.tasksInProgress() as string[]);
    this.tryingToLeave.set(true);
    // Only running tasks - an already paused one has nothing to log
    this._tasks
      .filter(task => !!task.start && task.paused !== 'true')
      .forEach(task => {
        this.pauseWorkTask(task);
      });
  }

  public openOverLimitPopup() {
    this.manufacturingOperation?.operationClaim.remainingEstimatedTimeInMinutes === 0
      ? this.alreadyOverclaimedWorklogPopupVisible.set(true)
      : this.overclaimedWorklogPopupVisible.set(true);
  }

  public getCurrentOperationMetadata() {
    return this.currentTaskMetadata;
  }

  public setCurrentOperationMetadata(data: TaskMetadata) {
    this.currentTaskMetadata = {
      dailyPlanId: data.dailyPlanId,
      technologicalProcessId: data.technologicalProcessId,
      operationNumber: data.operationNumber,
    };
  }

  public openTimeTracker() {
    this.openPopup.next();
  }

  private createRequestForChangeTimeComplaint(description: string, duration: number, preparationTime: number) {
    return this.dailyPlanService.createComplaint(this.currentTaskMetadata.dailyPlanId, {
      type: RequestForChangeType.TimeComplaint,
      description: description || 'Reklamace',
      stepTimeMinutes: duration,
      preparationTimeMinutes: preparationTime,
      isCreatedAutomatically: true,
    });
  }

  private logWork(
    task: TimeTrackerItem,
    operation: ManufacturingOperationDetailModel,
    returnMinutes = false,
    cb1?: () => void,
    cb2?: (overclaimedMinutes?: number) => void,
  ) {
    if (!operation) {
      this.endAction(task.taskNumber);
      throw 'Operation is not loaded.';
    }

    // Only a running task has time to log - a real segment shorter than one minute is logged as 1 minute
    const durationInMinutes = this.getRunningMinutes(task.taskNumber);

    if (!durationInMinutes) {
      cb1 && cb1();
      return;
    }

    const claimingOverLimit = operation.operationClaim && durationInMinutes > operation.operationClaim.remainingEstimatedTimeInMinutes;
    const duration = claimingOverLimit ? operation.operationClaim.remainingEstimatedTimeInMinutes : durationInMinutes;
    const requestForChangeMinutes = durationInMinutes - operation.operationClaim.remainingEstimatedTimeInMinutes;

    this.requestForChangeMinutes = requestForChangeMinutes;

    const workLog = {
      durationInMinutes: duration,
      recordType: RecordType.Claim,
    } as CreateWorkLogModel;

    if (duration) {
      let obs = this.timesheetService.createWorkLog(operation.operationClaim.id, workLog);
      const minutesToReturn = operation.operationClaim.remainingEstimatedTimeInMinutes - duration;
      if (returnMinutes && minutesToReturn) {
        const workLogReturn = {
          durationInMinutes: minutesToReturn,
          recordType: RecordType.Return,
        } as CreateWorkLogModel;
        obs = obs.pipe(switchMap(() => this.timesheetService.createWorkLog(operation.operationClaim.id, workLogReturn)));
      }
      obs.subscribe({
        next: () => {
          cb1 && cb1();
          this.snackbarService.addSnackbarAndTranslateIt({
            message: 'domains.timesheets.common.workLogged',
            iconType: 'success',
          });
        },
        error: () => this.onLogWorkFailed(task.taskNumber),
      });
    } else {
      // Nothing left to claim, only the state changes
      cb1 && cb1();
    }
    if (claimingOverLimit) {
      cb2 && cb2(requestForChangeMinutes);
    }
  }

  private getDataFromLocalStorage() {
    const defaultValue: any[] = [];
    try {
      const d = localStorage.getItem(timeTrackerLocalStorageKey);
      const parsedData = JSON.parse(d as string);
      return parsedData || defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }

  private saveDataToLocalStorage(data: any[]) {
    localStorage.setItem(timeTrackerLocalStorageKey, JSON.stringify(data));
  }

  /* Operation was not loaded or the work log was not saved - nothing changes, the buttons are released */
  private onLogWorkFailed(taskNumber: string) {
    this.endAction(taskNumber);
    this.snackbarService.addSnackbarAndTranslateIt({
      message: 'domains.timesheets.errors.logWork',
      iconType: 'error',
    });
  }

  /* Start / continue failed - return the task to what it was before, a newly added task is removed */
  private getStartRevert(task: TimeTrackerItem) {
    const previous = this.getTask(task.taskNumber);
    return () => (previous ? this.saveTask(previous) : this.removeTask(task));
  }

  /* Pause / stop failed, the operation is still running - the logged time is not counted again */
  private keepTaskRunning(task: TimeTrackerItem, stopPending = false) {
    this.saveTask({ ...task, start: Date.now().toString(), paused: 'false', stopPending });
  }

  /* Work is logged - remove the task from the tracker and change the operation state */
  private completeStop(task: TimeTrackerItem) {
    this.removeTask(task);
    if (task.triggeredFrom === 'daily-plan') {
      this.onStop.next({
        id: task.meta.dailyPlanId,
        taskNumber: task.taskNumber,
        revert: () => this.keepTaskRunning(task, true),
      });
    } else {
      this.endAction(task.taskNumber);
    }
  }

  /* Minutes of the running segment of the task stored in the tracker, 0 when the task is missing or paused */
  private getRunningMinutes(taskNumber: string) {
    const task = this.getTask(taskNumber);
    if (!task?.start || task.paused === 'true') return 0;

    const elapsedMs = Date.now() - +task.start;
    if (!(elapsedMs > 0)) return 0;

    return Math.max(1, Math.floor(elapsedMs / 1000 / 60));
  }
}
